// src/UserProfileModal.js
import React, { useState, useEffect } from "react";
import PhoneInput from "react-phone-number-input";
import { isValidPhoneNumber } from "libphonenumber-js";
import "react-phone-number-input/style.css";
import "./UserProfileModal.css";
import { useAuth } from "./contexts/AuthContext";
import ChangePasswordModal from "./components/CredentialManagement/modals/ChangePasswordModal";
import config from "./config";
import logger from "./utils/logger";

const UserProfileModal = ({ isOpen, onClose }) => {
  const { user, getAccessToken, refreshUserProfile } = useAuth();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);

  // Load user data when modal opens
  useEffect(() => {
    if (isOpen && user) {
      setFirstName(user.firstName || "");
      setLastName(user.lastName || "");
      setPhoneNumber(user.phoneNumber || "");
      setError(null);
      setSuccess(false);
      setValidationErrors({});
    }
  }, [isOpen, user]);

  const validateForm = () => {
    const errors = {};

    if (!firstName || firstName.trim() === "") {
      errors.firstName = "First name is required";
    }

    if (!lastName || lastName.trim() === "") {
      errors.lastName = "Last name is required";
    }

    if (!phoneNumber || phoneNumber.trim() === "") {
      errors.phoneNumber = "Phone number is required";
    } else if (!isValidPhoneNumber(phoneNumber)) {
      errors.phoneNumber = "Invalid phone number format";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const accessToken = await getAccessToken();
      const federatedUserId = user.federatedUserId;

      logger.log("[UserProfile] Starting profile update for:", federatedUserId);
      logger.log("[UserProfile] Update data:", {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phoneNumber: "***",
      });

      const response = await fetch(
        `${
          config.servicesApiEndpoint
        }/user/profile?federatedUserId=${encodeURIComponent(federatedUserId)}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "x-api-key": config.apiKey,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            phoneNumber: phoneNumber.trim(),
          }),
        }
      );

      logger.log("[UserProfile] Update response status:", response.status);

      if (!response.ok) {
        const errorData = await response.json();
        logger.error("[UserProfile] Error response:", errorData);
        logger.error("[UserProfile] Full error details:", {
          status: response.status,
          statusText: response.statusText,
          errorData,
        });
        throw new Error(
          errorData.message || errorData.error || "Failed to update profile"
        );
      }

      logger.log("[UserProfile] ✅ Update successful! Setting success=true");
      setSuccess(true);

      // Refresh user profile in context
      logger.log("[UserProfile] Refreshing user profile...");
      await refreshUserProfile(accessToken);
      logger.log("[UserProfile] ✅ User profile refreshed!");

      // Close modal after a short delay to show success message
      logger.log("[UserProfile] Closing modal in 1.5 seconds...");
      setTimeout(() => {
        logger.log("[UserProfile] Calling onClose()");
        onClose();
      }, 1500);
    } catch (err) {
      logger.error("[UserProfile] ❌ Error in handleSubmit catch block");
      logger.error("[UserProfile] Error updating profile:", err);
      logger.error("[UserProfile] Error name:", err.name);
      logger.error("[UserProfile] Error message:", err.message);
      logger.error("[UserProfile] Full error:", err);
      setError(err.message || "Failed to update profile. Please try again.");
    } finally {
      logger.log("[UserProfile] Setting isSaving=false");
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setError(null);
    setSuccess(false);
    setValidationErrors({});
    onClose();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="modal-overlay" onClick={handleCancel}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Edit Profile</h2>
          <button
            className="close-button"
            onClick={handleCancel}
            disabled={isSaving}
          >
            ×
          </button>
        </div>

        <div className="modal-body">
          {error && (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}

          {success && (
            <div className="success-message">
              <span className="success-icon">✓</span>
              Profile updated successfully!
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={user?.email || ""}
              disabled
              className="readonly-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="firstName">
              First Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="firstName"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              disabled={isSaving}
              className={validationErrors.firstName ? "input-error" : ""}
              placeholder="Enter your first name"
            />
            {validationErrors.firstName && (
              <span className="field-error">{validationErrors.firstName}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="lastName">
              Last Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="lastName"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              disabled={isSaving}
              className={validationErrors.lastName ? "input-error" : ""}
              placeholder="Enter your last name"
            />
            {validationErrors.lastName && (
              <span className="field-error">{validationErrors.lastName}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="phoneNumber">
              Phone Number <span className="required">*</span>
            </label>
            <PhoneInput
              id="phoneNumber"
              international
              defaultCountry="US"
              value={phoneNumber}
              onChange={setPhoneNumber}
              disabled={isSaving}
              className={
                validationErrors.phoneNumber ? "phone-input-error" : ""
              }
              placeholder="Enter phone number"
            />
            {validationErrors.phoneNumber && (
              <span className="field-error">
                {validationErrors.phoneNumber}
              </span>
            )}
            <span className="field-hint">
              Include country code (e.g., +1 555-555-5555)
            </span>
          </div>

          {/* Password Change Section */}
          <div className="form-section password-section">
            <button
              type="button"
              className="toggle-password-button"
              onClick={() => setShowChangePasswordModal(true)}
              disabled={isSaving}
            >
              Change Password
            </button>
          </div>
        </div>

        <div className="modal-footer">
          <button
            className="button button-secondary"
            onClick={handleCancel}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            className="button button-primary"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Save Profile"}
          </button>
        </div>
      </div>

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
        user={user}
      />
    </div>
  );
};

export default UserProfileModal;
